/**
 * personalizado.js (final + bloquea fly-to-cart cuando falta selección)
 */

(() => {
  const OPTION_NAME = 'Personaliza con tu nombre';

  const YES_VALUES = new Set(['si', 'sí']);
  const NO_VALUES = new Set(['no']);

  const PLACEHOLDER_TEXT = 'Selecciona una opción…';
  const PLACEHOLDER_ATTR = 'data-personalizado-placeholder';
  const ERROR_CLASS = 'personalizado-error';

  const DEBUG = false;
  const log = (...args) => DEBUG && console.log('[personalizado]', ...args);

  const norm = (v) => (v || '').trim().toLowerCase();
  const isYes = (v) => YES_VALUES.has(norm(v));
  const isNo = (v) => NO_VALUES.has(norm(v));
  const isChosen = (v) => isYes(v) || isNo(v);

  let didInitialReset = false;

  function getVariantPickerRoot() {
    return document.querySelector('variant-picker') || document;
  }

  function getSelect() {
    return document.querySelector(`select[name="options[${CSS.escape(OPTION_NAME)}]"]`);
  }

  function getUI() {
    return {
      uiWrap: document.querySelector('.js-personalizado-ui'),
      uiInput: document.querySelector('.js-personalizado-input'),
      errorMsg: document.querySelector('.js-personalizado-error-msg'),
    };
  }

  function getForms() {
    return Array.from(document.querySelectorAll('form[data-type="add-to-cart-form"]'));
  }

  function setHiddenForAllForms(value) {
    getForms().forEach((form) => {
      const hidden = form.querySelector('.js-personalizado-hidden');
      if (hidden) hidden.value = value;
    });
  }

  function getSelectWrapper(select) {
    return (
      select.closest('.variant-option') ||
      select.closest('.variant-option__select-wrapper') ||
      select.parentElement
    );
  }

  function showSelectError() {
    const select = getSelect();
    if (!select) return;

    const wrapper = getSelectWrapper(select);
    if (wrapper) wrapper.classList.add(ERROR_CLASS);

    const { errorMsg } = getUI();
    if (errorMsg) errorMsg.style.display = 'block';

    select.focus();
  }

  function clearSelectError() {
    const select = getSelect();
    if (!select) return;

    const wrapper = getSelectWrapper(select);
    if (wrapper) wrapper.classList.remove(ERROR_CLASS);

    const { errorMsg } = getUI();
    if (errorMsg) errorMsg.style.display = 'none';
  }

  function ensurePlaceholderOption() {
    const select = getSelect();
    if (!select) return;

    const existing = select.querySelector(`option[${PLACEHOLDER_ATTR}="true"]`);
    if (!existing) {
      const opt = document.createElement('option');
      opt.value = '';
      opt.textContent = PLACEHOLDER_TEXT;
      opt.disabled = true;
      opt.setAttribute(PLACEHOLDER_ATTR, 'true');
      select.insertBefore(opt, select.firstChild);
      log('Inserted placeholder option');
    }
  }

  function resetToPlaceholderOnce() {
    if (didInitialReset) return;

    const doReset = () => {
      const select = getSelect();
      const { uiWrap, uiInput } = getUI();
      if (!select || !uiWrap || !uiInput) return false;

      ensurePlaceholderOption();
      select.value = '';

      clearSelectError();
      uiWrap.style.display = 'none';
      uiInput.value = '';
      setHiddenForAllForms('');

      didInitialReset = true;
      log('Reset to placeholder (initial)');
      return true;
    };

    if (doReset()) return;
    setTimeout(() => doReset(), 50);
    setTimeout(() => doReset(), 250);
  }

  function sync() {
    const select = getSelect();
    const { uiWrap, uiInput } = getUI();
    if (!select || !uiWrap || !uiInput) return;

    ensurePlaceholderOption();

    if (!isChosen(select.value)) {
      uiWrap.style.display = 'none';
      uiInput.value = '';
      setHiddenForAllForms('');
      return;
    }

    clearSelectError();

    if (isNo(select.value)) {
      uiWrap.style.display = 'none';
      uiInput.value = '';
      setHiddenForAllForms('');
      return;
    }

    uiWrap.style.display = 'block';
    setHiddenForAllForms(uiInput.value.trim());
  }

  function snapshotButtonState(btn) {
    if (!btn) return;
    if (btn.dataset.personalizadoOriginalLabel) return;

    btn.dataset.personalizadoOriginalLabel = btn.textContent ?? '';
    btn.dataset.personalizadoHadDisabled = btn.disabled ? 'true' : 'false';
  }

  function restoreButtonState(btn) {
    if (!btn) return;

    const original = btn.dataset.personalizadoOriginalLabel;
    if (original != null) btn.textContent = original;

    const hadDisabled = btn.dataset.personalizadoHadDisabled === 'true';
    btn.disabled = hadDisabled;
  }

  function blockEventFully(e) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
  }

  function missingRequirements() {
    const select = getSelect();
    const { uiInput } = getUI();
    if (!select || !uiInput) return { missing: false, missingChoice: false, missingName: false };

    ensurePlaceholderOption();

    const missingChoice = !isChosen(select.value);
    const missingName = isYes(select.value) && uiInput.value.trim() === '';

    return {
      missing: missingChoice || missingName,
      missingChoice,
      missingName,
    };
  }

  function validateOrBlock(e, form, maybeButtonToRestore = null) {
    const select = getSelect();
    const { uiInput } = getUI();
    if (!select || !uiInput) return true;

    ensurePlaceholderOption();

    if (!isChosen(select.value)) {
      blockEventFully(e);
      showSelectError();

      if (maybeButtonToRestore) {
        restoreButtonState(maybeButtonToRestore);
        setTimeout(() => restoreButtonState(maybeButtonToRestore), 0);
      }
      return false;
    }

    if (isYes(select.value)) {
      const value = uiInput.value.trim();
      const hidden = form.querySelector('.js-personalizado-hidden');
      if (hidden) hidden.value = value;

      if (!value) {
        blockEventFully(e);
        uiInput.focus();

        if (maybeButtonToRestore) {
          restoreButtonState(maybeButtonToRestore);
          setTimeout(() => restoreButtonState(maybeButtonToRestore), 0);
        }
        return false;
      }
    } else {
      const hidden = form.querySelector('.js-personalizado-hidden');
      if (hidden) hidden.value = '';
    }

    return true;
  }

  function bindOnce() {
    const select = getSelect();
    const { uiWrap, uiInput } = getUI();
    const forms = getForms();

    if (!select || !uiWrap || !uiInput || forms.length === 0) return false;
    if (window.__personalizadoBound) return true;
    window.__personalizadoBound = true;

    log('Binding listeners');

    sync();

    // Cambios del picker
    const root = getVariantPickerRoot();
    root.addEventListener('change', () => {
      ensurePlaceholderOption();
      sync();
    });

    // Input -> hidden
    uiInput.addEventListener('input', () => {
      const s = getSelect();
      if (s && isYes(s.value)) setHiddenForAllForms(uiInput.value.trim());
    });

    // ✅ Intercepta eventos tempranos para bloquear fly-to-cart si falta selección/nombre
    const earlyEvents = ['pointerdown', 'mousedown', 'touchstart'];

    forms.forEach((form) => {
      const btns = form.querySelectorAll('button[type="submit"], input[type="submit"]');

      btns.forEach((btn) => {
        snapshotButtonState(btn);

        earlyEvents.forEach((evtName) => {
          btn.addEventListener(
            evtName,
            (e) => {
              const { missing, missingChoice, missingName } = missingRequirements();
              if (!missing) return;

              // Esto evita la animación fly-to-cart (se dispara en eventos tempranos)
              blockEventFully(e);

              if (missingChoice) showSelectError();
              if (missingName) {
                const { uiInput } = getUI();
                if (uiInput) uiInput.focus();
              }

              // Por si el theme alcanzó a tocar UI del botón
              restoreButtonState(btn);
              setTimeout(() => restoreButtonState(btn), 0);
            },
            true // ✅ captura
          );
        });

        // Click (también en captura) para evitar check / estados
        btn.addEventListener(
          'click',
          (e) => {
            validateOrBlock(e, form, btn);
          },
          true
        );
      });
    });

    // Submit (captura)
    forms.forEach((form) => {
      form.addEventListener(
        'submit',
        (e) => {
          validateOrBlock(e, form, null);
        },
        true
      );
    });

    return true;
  }

  function init() {
    resetToPlaceholderOnce();

    if (bindOnce()) return;

    let tries = 0;
    const t = setInterval(() => {
      tries++;
      if (bindOnce() || tries >= 20) clearInterval(t);
      ensurePlaceholderOption();
      sync();
    }, 250);

    const mo = new MutationObserver(() => {
      ensurePlaceholderOption();
      sync();
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  document.addEventListener('DOMContentLoaded', init);

  window.addEventListener('pageshow', () => {
    didInitialReset = false;
    init();
  });

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:block:select', init);

  function formatDateEs(date) {
  return date.toLocaleDateString('es-CO', {
    day: 'numeric',
    month: 'long'
  });
}

function addBusinessDays(startDate, businessDays) {
  const date = new Date(startDate);
  let addedDays = 0;

  while (addedDays < businessDays) {
    date.setDate(date.getDate() + 1);
    const day = date.getDay();

    // 0 = domingo, 6 = sábado
    if (day !== 0 && day !== 6) {
      addedDays++;
    }
  }

  return date;
}

function calculateEstimatedDelivery() {
  const element = document.getElementById('delivery-date');
  if (!element) return;

  const today = new Date();

  // Fecha límite: 7 de abril del año actual
  const currentYear = today.getFullYear();
  const cutoffDate = new Date(currentYear, 3, 7, 23, 59, 59); // Abril = 3

  let text = '';

  if (today <= cutoffDate) {
    text = 'Entrega estimada: 7 abril - 15 abril';
  } else {
    const minDate = addBusinessDays(today, 3);
    const maxDate = addBusinessDays(today, 5);

    text = `Entrega estimada: ${formatDateEs(minDate)} - ${formatDateEs(maxDate)}`;
  }

  element.textContent = text;
}

document.addEventListener('DOMContentLoaded', calculateEstimatedDelivery);
})();